import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { deserialize, serialize } from './serializeUtils';
let messageID = 0;
const getID = () => {
    const currentID = messageID;
    messageID = currentID === Number.MAX_SAFE_INTEGER ? 0 : messageID + 1;
    return `${currentID}`;
};
const handleReceiveResponse = async (id, messageEndpoint) => new Promise((resolve, reject) => {
    function listener(event) {
        if (event.id === id && event.type !== 'NEXT') {
            if (event.type === 'RETURN') {
                resolve(deserialize(event.value));
            }
            if (event.type === 'ERROR') {
                reject(deserialize(event.value));
            }
        }
    }
    messageEndpoint.addEventListener(listener);
});
async function* asyncGenerator(messageEndpoint, method, network, options) {
    const id = getID();
    messageEndpoint.postMessage({
        type: 'ASYNCITERABLE',
        method,
        args: [serialize(network), serialize(options)],
        id,
    });
    while (true) {
        const rawActionIterator = handleReceiveResponse(id, messageEndpoint);
        messageEndpoint.postMessage({ type: 'NEXT', id });
        const result = await rawActionIterator;
        if (result.done) {
            break;
        }
        yield result.value;
    }
}
export class RemoteUserAccountProvider {
    constructor({ endpoint }) {
        this.messageEndpoint = endpoint;
        this.userAccountsInternal$ = new BehaviorSubject([]);
        this.currentAccountInternal$ = new BehaviorSubject(undefined);
        this.networksInternal$ = new BehaviorSubject([]);
        this.messageEndpoint.addEventListener((event) => {
            switch (event.type) {
                case 'OBSERVABLE': {
                    if (event.id === 'userAccounts$') {
                        this.userAccountsInternal$.next(deserialize(event.value));
                    }
                    if (event.id === 'currentUserAccount$') {
                        this.currentAccountInternal$.next(deserialize(event.value));
                    }
                    if (event.id === 'networks$') {
                        this.networksInternal$.next(deserialize(event.value));
                    }
                    break;
                }
                case 'ERROR': {
                    try {
                        deserialize(event.value);
                    }
                    catch (error) {
                        if (event.id === 'userAccounts$') {
                            this.userAccountsInternal$.error(error);
                        }
                        if (event.id === 'currentUserAccount$') {
                            this.currentAccountInternal$.error(error);
                        }
                        if (event.id === 'networks$') {
                            this.networksInternal$.error(error);
                        }
                    }
                    break;
                }
                case 'RETURN': {
                    if (event.id === 'userAccounts$') {
                        this.userAccountsInternal$.complete();
                    }
                    if (event.id === 'currentUserAccount$') {
                        this.currentAccountInternal$.complete();
                    }
                    if (event.id === 'networks$') {
                        this.networksInternal$.complete();
                    }
                    break;
                }
                default:
            }
        });
        this.userAccounts$ = this.userAccountsInternal$;
        this.currentUserAccount$ = this.currentAccountInternal$.pipe(distinctUntilChanged());
        this.networks$ = this.networksInternal$;
    }
    getCurrentUserAccount() {
        return this.currentAccountInternal$.getValue();
    }
    async selectUserAccount(userAccountID) {
        const result = await this.handleRelayMethod({
            method: 'selectUserAccount',
            args: [userAccountID],
        });
        return result.result;
    }
    async deleteUserAccount(userAccountID) {
        const result = await this.handleRelayMethod({ method: 'deleteUserAccount', args: [userAccountID] });
        return result.result;
    }
    getUserAccounts() {
        return this.userAccountsInternal$.getValue();
    }
    getNetworks() {
        return this.networksInternal$.getValue();
    }
    async updateUserAccountName(options) {
        const result = await this.handleRelayMethod({ method: 'updateUserAccountName', args: [options] });
        return result.result;
    }
    async getBlockCount(network) {
        const result = await this.handleRelayMethod({ method: 'getBlockCount', args: [network] });
        return result.result;
    }
    async getAccount(network, address) {
        const result = await this.handleRelayMethod({ method: 'getAccount', args: [network, address] });
        return result.result;
    }
    iterBlocks(network, options) {
        return asyncGenerator(this.messageEndpoint, 'iterBlocks', network, options);
    }
    iterActionsRaw(network, options) {
        return asyncGenerator(this.messageEndpoint, 'iterActionsRaw', network, options);
    }
    async transfer(transfers, options) {
        return this.handleMethodWithConfirmation({
            method: 'transfer',
            args: [transfers, options],
        });
    }
    async claim(options) {
        return this.handleMethodWithConfirmation({ method: 'claim', args: [options] });
    }
    async invoke(contract, method, params, paramsZipped, verify, options, sourceMaps) {
        return this.handleMethodWithConfirmation({
            method: 'invoke',
            args: [contract, method, params, paramsZipped, verify, options, sourceMaps],
        });
    }
    async invokeSend(contract, method, params, paramsZipped, transfer, options, sourceMaps) {
        return this.handleMethodWithConfirmation({
            method: 'invokeSend',
            args: [contract, method, params, paramsZipped, transfer, options, sourceMaps],
        });
    }
    async invokeCompleteSend(contract, method, params, paramsZipped, hash, options, sourceMaps) {
        return this.handleMethodWithConfirmation({
            method: 'invokeCompleteSend',
            args: [contract, method, params, paramsZipped, hash, options, sourceMaps],
        });
    }
    async invokeRefundAssets(contract, method, params, paramsZipped, hash, options, sourceMaps) {
        return this.handleMethodWithConfirmation({
            method: 'invokeRefundAssets',
            args: [contract, method, params, paramsZipped, hash, options, sourceMaps],
        });
    }
    async invokeClaim(contract, method, params, paramsZipped, options, sourceMaps) {
        return this.handleMethodWithConfirmation({
            method: 'invokeClaim',
            args: [contract, method, params, paramsZipped, options, sourceMaps],
        });
    }
    async call(network, contract, method, params) {
        const result = await this.handleRelayMethod({
            method: 'call',
            args: [network, contract, method, params],
        });
        return result.result;
    }
    async handleRelayMethod({ method, args, confirm = false, }) {
        const id = getID();
        this.messageEndpoint.postMessage({
            type: 'METHOD',
            method,
            confirm,
            args: args.map(serialize),
            id,
        });
        const result = await handleReceiveResponse(id, this.messageEndpoint);
        return { id, result };
    }
    handleConfirmation(id) {
        return async (options) => {
            this.messageEndpoint.postMessage({
                type: 'CONFIRMATION',
                method: 'confirmed',
                args: [serialize(options)],
                id,
            });
            return handleReceiveResponse(id, this.messageEndpoint);
        };
    }
    async handleMethodWithConfirmation({ method, args, }) {
        const result = await this.handleRelayMethod({
            method,
            args,
            confirm: true,
        });
        return {
            ...result.result,
            confirmed: this.handleConfirmation(result.id),
        };
    }
}

//# sourceMappingURL=RemoteUserAccountProvider.js.map
